import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { collection, getDocs, query, where, doc, getDoc, limit } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Spinner,
  Alert,
} from 'react-bootstrap';
import Layout from '../../components/ui/Layout';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import PublicProductsSubscriptionModal from '../../components/public/PublicProductsSubscriptionModal';
import usePublicProductsSubscription from '../../hooks/usePublicProductsSubscription';
import { resolveProductIdFromSlug } from '../../utils/resolveProductIdFromSlug';
import { SITE_URL, getProductShareSlug } from '../../constants/site';
import { db } from '../../firebase';
import { createLogger } from '../../utils/logger';
import ProductImage from '../../components/ui/ProductImage';

const logger = createLogger('ProductAnalysis');

const CHART_COLORS = ['#0d6efd', '#198754', '#ffc107', '#dc3545', '#6f42c1', '#20c997', '#fd7e14', '#6610f2'];

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const truncateLabel = (label, max = 14) =>
  label && label.length > max ? `${label.slice(0, max)}…` : label || 'Unknown';

export default function ProductAnalysis() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const {
    analyticsUnlocked,
    mustSubscribe,
    resolvingAccess,
    subscriptionAmount,
    subscriptionCurrency,
    payingSubscription,
    subscriptionPayError,
    handleSubscriptionPay,
    currentUser,
  } = usePublicProductsSubscription();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [product, setProduct] = useState(null);
  const [storeData, setStoreData] = useState([]);
  const [categoryBenchmark, setCategoryBenchmark] = useState(null);
  const [salesStats, setSalesStats] = useState({ totalSold: 0, orderCount: 0 });

  useEffect(() => {
    if (analyticsUnlocked !== true) {
      setLoading(resolvingAccess);
      return;
    }

    let cancelled = false;

    const fetchAnalysis = async () => {
      setLoading(true);
      setError('');

      try {
        const productId = await resolveProductIdFromSlug(slug);
        if (!productId) {
          if (!cancelled) setError('Product not found.');
          return;
        }

        const productSnap = await getDoc(doc(db, 'Products', productId));
        if (!productSnap.exists()) {
          if (!cancelled) setError('Product not found.');
          return;
        }
        const fetchedProduct = { id: productSnap.id, ...productSnap.data() };
        if (!cancelled) setProduct(fetchedProduct);

        const inventorySnap = await getDocs(
          query(
            collection(db, 'Inventory'),
            where('product_id', '==', productId),
            where('quantity', '>', 0)
          )
        );

        const stores = await Promise.all(
          inventorySnap.docs.map(async (invDoc) => {
            const inv = invDoc.data();
            const smSnap = await getDoc(doc(db, 'Supermarkets', inv.supermarket_id));
            const smName = smSnap.exists() ? smSnap.data().display_name : 'Unknown Store';
            const salePrice = typeof inv.sale_price === 'number' ? inv.sale_price : 0;
            const quantity = typeof inv.quantity === 'number' ? inv.quantity : 0;
            const deals = Array.isArray(inv.discount_deals) ? inv.discount_deals : [];
            const bestDealSavings =
              deals.length > 0
                ? Math.max(
                    ...deals.map((deal) => {
                      const regular = salePrice * deal.quantity;
                      return regular > 0 ? ((regular - deal.price) / regular) * 100 : 0;
                    })
                  )
                : 0;

            return {
              supermarketId: inv.supermarket_id,
              name: smName,
              price: salePrice,
              quantity,
              deliveryEnabled: smSnap.exists() ? Boolean(smSnap.data().delivery_enabled) : false,
              dealCount: deals.length,
              bestDealSavings,
            };
          })
        );

        stores.sort((a, b) => a.price - b.price);
        if (!cancelled) setStoreData(stores);

        const category = fetchedProduct.category;
        if (category) {
          const categorySnap = await getDocs(
            query(collection(db, 'Products'), where('category', '==', category), limit(100))
          );
          const categoryProductIds = categorySnap.docs.map((d) => d.id);

          const categoryPrices = [];
          await Promise.all(
            categoryProductIds.slice(0, 50).map(async (catProductId) => {
              const catInvSnap = await getDocs(
                query(
                  collection(db, 'Inventory'),
                  where('product_id', '==', catProductId),
                  where('quantity', '>', 0)
                )
              );
              const prices = catInvSnap.docs
                .map((d) => d.data().sale_price)
                .filter((p) => typeof p === 'number' && p > 0);
              if (prices.length > 0) {
                categoryPrices.push(Math.min(...prices));
              }
            })
          );

          if (categoryPrices.length > 0 && !cancelled) {
            const avg = categoryPrices.reduce((s, p) => s + p, 0) / categoryPrices.length;
            const productCheapest =
              stores.length > 0 ? Math.min(...stores.map((s) => s.price)) : null;
            setCategoryBenchmark({
              category,
              averagePrice: avg,
              minPrice: Math.min(...categoryPrices),
              maxPrice: Math.max(...categoryPrices),
              productCount: categoryPrices.length,
              productCheapest,
              vsAverage:
                productCheapest != null && avg > 0
                  ? ((avg - productCheapest) / avg) * 100
                  : null,
            });
          }
        }

        try {
          const transactionsSnap = await getDocs(collection(db, 'Transactions'));
          const completedStatuses = ['completed', 'packed_awaiting_pickup', 'awaiting_packing', 'payment_successful'];
          let totalSold = 0;
          let orderCount = 0;

          transactionsSnap.docs.forEach((txDoc) => {
            const data = txDoc.data();
            if (!completedStatuses.includes(data.status)) return;
            const items = data.cartItems || data.products || [];
            if (!Array.isArray(items)) return;

            let foundInOrder = false;
            items.forEach((item) => {
              const itemProductId = item.productId || item.product_id;
              if (itemProductId === productId) {
                const qty = typeof item.quantity === 'number' ? item.quantity : 0;
                totalSold += qty;
                foundInOrder = true;
              }
            });
            if (foundInOrder) orderCount += 1;
          });

          if (!cancelled) setSalesStats({ totalSold, orderCount });
        } catch (txErr) {
          logger.warn('[ProductAnalysis] Could not fetch sales data:', txErr);
        }
      } catch (err) {
        logger.error('[ProductAnalysis] Failed to load analysis:', err);
        if (!cancelled) setError('Failed to load product analysis.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAnalysis();
    return () => {
      cancelled = true;
    };
  }, [slug, analyticsUnlocked, resolvingAccess]);

  const priceChartData = useMemo(
    () =>
      storeData.map((s) => ({
        name: truncateLabel(s.name),
        fullName: s.name,
        price: s.price,
        delivery: s.deliveryEnabled ? 'Yes' : 'No',
      })),
    [storeData]
  );

  const stockChartData = useMemo(
    () =>
      storeData.map((s) => ({
        name: truncateLabel(s.name),
        fullName: s.name,
        stock: s.quantity,
      })),
    [storeData]
  );

  const stockShareData = useMemo(() => {
    const total = storeData.reduce((sum, s) => sum + s.quantity, 0);
    if (total === 0) return [];
    return storeData.map((s) => ({
      name: truncateLabel(s.name, 12),
      value: s.quantity,
      percent: ((s.quantity / total) * 100).toFixed(1),
    }));
  }, [storeData]);

  const benchmarkChartData = useMemo(() => {
    if (!categoryBenchmark) return [];
    const rows = [
      { label: 'Category avg', value: categoryBenchmark.averagePrice },
      { label: 'Category low', value: categoryBenchmark.minPrice },
      { label: 'Category high', value: categoryBenchmark.maxPrice },
    ];
    if (categoryBenchmark.productCheapest != null) {
      rows.push({ label: 'This product', value: categoryBenchmark.productCheapest });
    }
    return rows;
  }, [categoryBenchmark]);

  const dealChartData = useMemo(
    () =>
      storeData
        .filter((s) => s.dealCount > 0)
        .map((s) => ({
          name: truncateLabel(s.name),
          fullName: s.name,
          savings: Number(s.bestDealSavings.toFixed(1)),
          deals: s.dealCount,
        })),
    [storeData]
  );

  const summary = useMemo(() => {
    if (storeData.length === 0) return null;
    const prices = storeData.map((s) => s.price);
    const cheapest = storeData[0];
    const priciest = storeData[storeData.length - 1];
    const totalStock = storeData.reduce((sum, s) => sum + s.quantity, 0);
    const deliveryStores = storeData.filter((s) => s.deliveryEnabled).length;

    return {
      cheapest,
      priciest,
      priceSpread: priciest.price - cheapest.price,
      storeCount: storeData.length,
      totalStock,
      deliveryStores,
      avgPrice: prices.reduce((s, p) => s + p, 0) / prices.length,
    };
  }, [storeData]);

  const breadcrumbItems = product
    ? [
        { path: '/', label: 'Home' },
        { path: '/products', label: 'Products' },
        { label: `${product.display_name || 'Product'} Analysis` },
      ]
    : [
        { path: '/', label: 'Home' },
        { path: '/products', label: 'Products' },
        { label: 'Product Analysis' },
      ];

  const seoTitle = product
    ? `${product.display_name} Analysis | Pack My Cart`
    : 'Product Analysis | Pack My Cart';

  if (resolvingAccess) {
    return (
      <Layout>
        <Container className="mt-4 text-center py-5">
          <Spinner animation="border" role="status" className="mb-3" />
          <p className="text-muted mb-0">Checking access…</p>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={`${SITE_URL}/products/analysis/${slug}`} />
      </Helmet>

      <Container className="mt-4 px-3 mb-5">
        <Row className="align-items-center mb-1">
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
              &larr; Go Back
            </Button>
          </Col>
          <Col>
            <BreadcrumbNavigation items={breadcrumbItems} />
          </Col>
        </Row>

        {analyticsUnlocked === true && (
          <>
            {loading && (
              <div className="text-center py-5">
                <Spinner animation="border" role="status" className="mb-3" />
                <p className="text-muted mb-0">Loading product analysis…</p>
              </div>
            )}

            {!loading && error && (
              <Alert variant="danger">
                {error}
                <div className="mt-2">
                  <Link to="/products" className="alert-link">
                    Back to products
                  </Link>
                </div>
              </Alert>
            )}

            {!loading && !error && product && (
              <>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-2">
                  <div>
                    <h1 className="mb-1">{product.display_name || 'Product Analysis'}</h1>
                    <p className="text-muted mb-0">
                      Detailed price, availability, and category insights for subscribers
                    </p>
                  </div>
                  <Badge bg="primary" className="fs-6">
                    <i className="fas fa-chart-bar me-1" />
                    Subscriber Analysis
                  </Badge>
                </div>

                <Row className="g-4 mb-4">
                  <Col xs={12} md={4}>
                    <Card className="h-100 shadow-sm">
                      <Card.Body>
                        {product.media?.[0]?.url ? (
                          <ProductImage
                            imagePath={product.media[0].url}
                            altText={product.display_name}
                          />
                        ) : null}
                        <div className="mt-3">
                          <Badge bg="secondary" className="me-2">
                            {product.category || 'Uncategorized'}
                          </Badge>
                          {summary?.deliveryStores > 0 && (
                            <Badge bg="success">
                              <i className="fas fa-truck me-1" />
                              {summary.deliveryStores} deliver
                            </Badge>
                          )}
                        </div>
                        <p className="mt-3 mb-0 small text-muted">
                          {product.description || 'No description available.'}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} md={8}>
                    <Row className="g-3">
                      {summary && (
                        <>
                          <Col xs={6} lg={3}>
                            <Card className="h-100 border-success shadow-sm">
                              <Card.Body className="text-center py-3">
                                <div className="text-muted small">Best price</div>
                                <div className="fs-4 fw-bold text-success">
                                  {formatCurrency(summary.cheapest.price)}
                                </div>
                                <div className="small text-truncate">{summary.cheapest.name}</div>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col xs={6} lg={3}>
                            <Card className="h-100 border-primary shadow-sm">
                              <Card.Body className="text-center py-3">
                                <div className="text-muted small">Avg price</div>
                                <div className="fs-4 fw-bold text-primary">
                                  {formatCurrency(summary.avgPrice)}
                                </div>
                                <div className="small">{summary.storeCount} stores</div>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col xs={6} lg={3}>
                            <Card className="h-100 border-warning shadow-sm">
                              <Card.Body className="text-center py-3">
                                <div className="text-muted small">Price spread</div>
                                <div className="fs-4 fw-bold text-warning">
                                  {formatCurrency(summary.priceSpread)}
                                </div>
                                <div className="small">High vs low</div>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col xs={6} lg={3}>
                            <Card className="h-100 border-info shadow-sm">
                              <Card.Body className="text-center py-3">
                                <div className="text-muted small">Total stock</div>
                                <div className="fs-4 fw-bold text-info">{summary.totalStock}</div>
                                <div className="small">{salesStats.totalSold} sold</div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </>
                      )}
                    </Row>

                    {categoryBenchmark?.vsAverage != null && (
                      <Alert
                        variant={categoryBenchmark.vsAverage >= 0 ? 'success' : 'warning'}
                        className="mt-3 mb-0"
                      >
                        {categoryBenchmark.vsAverage >= 0 ? (
                          <>
                            This product&apos;s best price is{' '}
                            <strong>{categoryBenchmark.vsAverage.toFixed(1)}% below</strong> the{' '}
                            {categoryBenchmark.category} category average (
                            {formatCurrency(categoryBenchmark.averagePrice)}).
                          </>
                        ) : (
                          <>
                            This product&apos;s best price is{' '}
                            <strong>
                              {Math.abs(categoryBenchmark.vsAverage).toFixed(1)}% above
                            </strong>{' '}
                            the {categoryBenchmark.category} category average (
                            {formatCurrency(categoryBenchmark.averagePrice)}).
                          </>
                        )}
                      </Alert>
                    )}
                  </Col>
                </Row>

                {storeData.length === 0 ? (
                  <Alert variant="info">This product is not currently available at any store.</Alert>
                ) : (
                  <Row className="g-4">
                    <Col xs={12} lg={6}>
                      <Card className="shadow-sm h-100">
                        <Card.Header>
                          <strong>
                            <i className="fas fa-dollar-sign me-2 text-success" />
                            Price by store
                          </strong>
                        </Card.Header>
                        <Card.Body>
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={priceChartData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} interval={0} />
                              <YAxis tickFormatter={(v) => `$${v}`} />
                              <Tooltip
                                formatter={(value) => [formatCurrency(value), 'Price']}
                                labelFormatter={(_, payload) =>
                                  payload?.[0]?.payload?.fullName || ''
                                }
                              />
                              <Legend />
                              <Bar dataKey="price" name="Sale price" fill="#198754" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col xs={12} lg={6}>
                      <Card className="shadow-sm h-100">
                        <Card.Header>
                          <strong>
                            <i className="fas fa-boxes me-2 text-primary" />
                            Stock availability
                          </strong>
                        </Card.Header>
                        <Card.Body>
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={stockChartData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} interval={0} />
                              <YAxis allowDecimals={false} />
                              <Tooltip
                                labelFormatter={(_, payload) =>
                                  payload?.[0]?.payload?.fullName || ''
                                }
                              />
                              <Legend />
                              <Bar dataKey="stock" name="Units in stock" fill="#0d6efd" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Card.Body>
                      </Card>
                    </Col>

                    {stockShareData.length > 0 && (
                      <Col xs={12} lg={6}>
                        <Card className="shadow-sm h-100">
                          <Card.Header>
                            <strong>
                              <i className="fas fa-chart-pie me-2 text-info" />
                              Stock distribution
                            </strong>
                          </Card.Header>
                          <Card.Body>
                            <ResponsiveContainer width="100%" height={280}>
                              <PieChart>
                                <Pie
                                  data={stockShareData}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={95}
                                  label={({ name, percent }) => `${name} (${percent}%)`}
                                >
                                  {stockShareData.map((entry, index) => (
                                    <Cell
                                      key={entry.name}
                                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => [`${value} units`, 'Stock']} />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}

                    {benchmarkChartData.length > 0 && (
                      <Col xs={12} lg={6}>
                        <Card className="shadow-sm h-100">
                          <Card.Header>
                            <strong>
                              <i className="fas fa-balance-scale me-2 text-warning" />
                              Category benchmark
                              {categoryBenchmark?.category ? ` — ${categoryBenchmark.category}` : ''}
                            </strong>
                          </Card.Header>
                          <Card.Body>
                            <ResponsiveContainer width="100%" height={280}>
                              <BarChart data={benchmarkChartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" />
                                <YAxis tickFormatter={(v) => `$${v}`} />
                                <Tooltip formatter={(value) => [formatCurrency(value), 'Price']} />
                                <Legend />
                                <Bar dataKey="value" name="Price" fill="#ffc107" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                            <p className="small text-muted mb-0 mt-2">
                              Compared across {categoryBenchmark.productCount} products in this
                              category.
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}

                    {dealChartData.length > 0 && (
                      <Col xs={12}>
                        <Card className="shadow-sm">
                          <Card.Header>
                            <strong>
                              <i className="fas fa-tags me-2 text-danger" />
                              Bulk deal savings by store
                            </strong>
                          </Card.Header>
                          <Card.Body>
                            <ResponsiveContainer width="100%" height={260}>
                              <BarChart data={dealChartData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} interval={0} />
                                <YAxis tickFormatter={(v) => `${v}%`} />
                                <Tooltip
                                  formatter={(value, name) =>
                                    name === 'savings' ? [`${value}%`, 'Best deal savings'] : [value, 'Deals']
                                  }
                                  labelFormatter={(_, payload) =>
                                    payload?.[0]?.payload?.fullName || ''
                                  }
                                />
                                <Legend />
                                <Bar
                                  dataKey="savings"
                                  name="Best deal savings %"
                                  fill="#dc3545"
                                  radius={[4, 4, 0, 0]}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}

                    <Col xs={12}>
                      <Card className="shadow-sm">
                        <Card.Header>
                          <strong>Store breakdown</strong>
                        </Card.Header>
                        <Card.Body className="p-0">
                          <div className="table-responsive">
                            <table className="table table-striped mb-0">
                              <thead>
                                <tr>
                                  <th>Store</th>
                                  <th>Price</th>
                                  <th>Stock</th>
                                  <th>Delivery</th>
                                  <th>Deals</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {storeData.map((store) => (
                                  <tr key={store.supermarketId}>
                                    <td>{store.name}</td>
                                    <td className="fw-bold text-success">
                                      {formatCurrency(store.price)}
                                    </td>
                                    <td>{store.quantity}</td>
                                    <td>
                                      {store.deliveryEnabled ? (
                                        <Badge bg="success">Yes</Badge>
                                      ) : (
                                        <Badge bg="secondary">Pickup</Badge>
                                      )}
                                    </td>
                                    <td>{store.dealCount > 0 ? store.dealCount : '—'}</td>
                                    <td>
                                      <Link
                                        to={`/supermarkets/${store.supermarketId}/products/${getProductShareSlug(product)}`}
                                        className="btn btn-sm btn-outline-primary"
                                      >
                                        View
                                      </Link>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </>
        )}

        <PublicProductsSubscriptionModal
          show={mustSubscribe && analyticsUnlocked === false}
          amount={subscriptionAmount}
          currency={subscriptionCurrency}
          isLoggedIn={Boolean(currentUser)}
          paying={payingSubscription}
          errorMessage={subscriptionPayError}
          onPay={handleSubscriptionPay}
          onGoBack={() => navigate(-1)}
        />
      </Container>
    </Layout>
  );
}
